import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { CreatePlanChatStudio } from '@/components/createPlan/CreatePlanChatStudio';
import { VideoThumb } from '@/components/channel/VideoThumb';
import { useChannelOverview } from '@/components/channel/ChannelPerformanceBand';
import { YouTubeDisconnectButton } from '@/components/channel/YouTubeDisconnectButton';

function ideaVideo(brief, videos) {
  const ids = new Set((brief?.evidence_video_ids || []).filter(Boolean));
  const topic = String(brief?.topic || '').toLowerCase();
  return (
    videos.find((video) => ids.has(video.video_id))
    || videos.find((video) => topic && String(video.title || '').toLowerCase().includes(topic))
    || null
  );
}

function chatSeed(brief) {
  const kind = brief.format === 'short' ? 'YouTube Short' : 'YouTube video';
  const tags = (brief.suggested_tags || []).filter(Boolean).join(', ');
  return {
    text: [
      `Make this ${kind}: ${brief.working_title || brief.topic}`,
      '',
      brief.hook || '',
      '',
      brief.why || '',
      tags ? `\nTags: ${tags}` : '',
    ].join('\n').trim(),
  };
}

export function ChannelNextIdeaPage() {
  const { ideaId } = useParams();
  const navigate = useNavigate();
  const { data, loading } = useChannelOverview('28d', '', '', '/dashboard');
  const [chatOpen, setChatOpen] = useState(false);
  const briefs = Array.isArray(data?.recommendations) ? data.recommendations : [];
  const videos = Array.isArray(data?.videos) ? data.videos : [];
  const brief = briefs.find((item, index) => (item.id || String(index)) === ideaId) || null;
  const sample = ideaVideo(brief, videos);
  const seed = useMemo(() => (brief ? chatSeed(brief) : null), [brief]);

  return (
    <main className="max-w-3xl p-4 sm:p-8 space-y-5 font-jakarta overflow-x-hidden">
      <div className="flex items-center justify-between gap-3">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-xs font-medium text-[#8b95a7] hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" />
          Home
        </Link>
        <YouTubeDisconnectButton />
      </div>

      {loading && !data ? <div className="h-40 rounded-[28px] bg-white/[0.03] animate-pulse" /> : null}

      {!loading && data && !data.connected ? (
        <p className="text-sm text-[#9aa6b8]">Connect YouTube to see ideas for your channel.</p>
      ) : null}

      {!loading && data?.connected && !brief ? (
        <p className="text-sm text-[#9aa6b8]">That idea is not in the current list.</p>
      ) : null}

      {data?.connected && brief ? (
        <article className="space-y-5">
          <div className="flex flex-col sm:flex-row gap-5 sm:items-end">
            <VideoThumb video={sample || { video_id: (brief.evidence_video_ids || [])[0] }} className="h-36 w-56 rounded-[22px]" />
            <div className="min-w-0 space-y-2">
              <p className="text-[11px] uppercase tracking-wide text-[#5eead4]">
                {brief.format === 'short' ? 'Short' : 'Video'}
                {brief.weekday ? ` · ${brief.weekday}` : ''}
              </p>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white">{brief.working_title}</h1>
              <p className="text-sm text-[#c5cedb]">{brief.hook}</p>
            </div>
          </div>
          <p className="text-sm text-[#9aa6b8] max-w-2xl">{brief.why}</p>
          {(brief.suggested_tags || []).length > 0 ? (
            <p className="text-xs text-[#7d8799]">{brief.suggested_tags.join(' · ')}</p>
          ) : null}
          <button
            type="button"
            onClick={() => setChatOpen(true)}
            className="px-5 py-2.5 rounded-full bg-[#34d399] text-[#06211b] text-sm font-bold"
          >
            Chat
          </button>
        </article>
      ) : null}

      <CreatePlanChatStudio
        open={chatOpen && Boolean(seed)}
        seed={seed}
        navigate={navigate}
        onClose={() => setChatOpen(false)}
      />
    </main>
  );
}

export default ChannelNextIdeaPage;
